import type { TooltipWalkthroughStep } from '@unifyapps/defs/types/page/walkthrough';

/**
 * Resolves effective interaction settings for walkthrough steps based on goToNextStepOnClickOf.
 *
 * When goToNextStepOnClickOf is set, we need to force certain interaction settings to ensure
 * clicks work correctly:
 * - If goToNextStepOnClickOf === 'outside': Force enableInteractionWithPage to false so backdrop
 *   blocks clicks and advances the step
 * - If goToNextStepOnClickOf === 'element': Force enableInteractionWithTargetElement to false so
 *   the target element's normal interactions are disabled and only our walkthrough click handler
 *   can advance the step
 *
 * This ensures that when click-to-advance is enabled, the necessary elements have the correct
 * interaction settings for the click handlers to work properly.
 */
export function resolveWalkthroughInteractionSettings(step: TooltipWalkthroughStep): {
  enableInteractionWithPage: boolean;
  enableInteractionWithTargetElement: boolean;
} {
  const { goToNextStepOnClickOf, enableInteractionWithPage, enableInteractionWithTargetElement } =
    step;

  let resolvedEnableInteractionWithPage = enableInteractionWithPage ?? true;
  let resolvedEnableInteractionWithTargetElement = enableInteractionWithTargetElement ?? true;

  // When clicking outside advances the step, force enableInteractionWithPage to false
  // so backdrop blocks clicks and our click handler can capture them
  if (goToNextStepOnClickOf === 'outside') {
    resolvedEnableInteractionWithPage = false;
  }

  // When clicking the element advances the step, force enableInteractionWithTargetElement to false
  // so the element's normal click handlers don't interfere and only our walkthrough handler fires
  if (goToNextStepOnClickOf === 'element') {
    resolvedEnableInteractionWithTargetElement = false;
  }

  return {
    enableInteractionWithPage: resolvedEnableInteractionWithPage,
    enableInteractionWithTargetElement: resolvedEnableInteractionWithTargetElement,
  };
}
